import { useQuery } from "@tanstack/react-query"
import { CircleAlert, FileDown, Lock } from "lucide-react"
import { useRef, useState } from "react"
import { useParams } from "react-router"

import { api } from "@/api/cliente"
import { LogoSetieca } from "@/componentes/marca/LogoSetieca"
import { pdfDeLaDisponibilidad } from "@/componentes/reportes/pdfDisponibilidad"
import { descargar, logoComoPng } from "@/componentes/reportes/pdfReporte"
import type { Saldo } from "@/componentes/reportes/tipos"
import type { CuentaDisponible, Disponibilidad } from "@/componentes/reportes/tiposDisponibilidad"
import { monto } from "@/componentes/tesoreria/dinero"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

/**
 * La disponibilidad bancaria de un día, desde el enlace del aviso de las 7:15
 * (`/d/<token>`, 13/10). **Sin sesión, sin menú y sin poder tocar nada**, con el mismo
 * aspecto que el reporte de cuentas por pagar: membrete, total por moneda arriba y una
 * fila por cuenta, que en el teléfono se apila en tarjetas.
 *
 * ⚠ **Es la FOTO de esa mañana**, no el saldo de ahora: lo que entró o salió después se
 * ve en Tesorería.
 *
 * ⚠ **Las cuentas cuyo saldo engaña lo dicen en «Notas», al pie** —la que admite
 * sobregiro y el conducto—, y un negativo se pinta en rojo.
 */

export function DisponibilidadPublica() {
  const { token = "" } = useParams()
  const marca = useRef<HTMLDivElement>(null)
  const [generando, setGenerando] = useState(false)
  const [fallo, setFallo] = useState(false)
  const consulta = useQuery({
    queryKey: ["publico", "disponibilidad", token],
    queryFn: () => api.get<Disponibilidad>(`/api/v1/publico/disponibilidad/${token}`),
    retry: false,
  })

  if (consulta.isPending) {
    return (
      <p role="status" className="p-10 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }
  if (consulta.isError || !consulta.data) {
    return (
      <div className="mx-auto max-w-lg p-10 text-center">
        <CircleAlert size={28} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
        <h1 className="mt-3 text-lg font-semibold">Este enlace ya no vale</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          Puede haber caducado. El del próximo día hábil trae los saldos nuevos.
        </p>
      </div>
    )
  }

  const d = consulta.data
  const tenue = { color: "var(--fg-muted)" }
  const borde = { borderColor: "var(--border)" }
  const rojo = { color: "var(--destructive)" }
  const conDos = d.sistema2_leido || d.sistema2_error !== null

  async function exportar() {
    setGenerando(true)
    setFallo(false)
    try {
      const logo = await logoComoPng(
        marca.current?.querySelector("svg") ?? null,
        "rgb(122, 27, 56)",
      )
      descargar(
        await pdfDeLaDisponibilidad(d, logo),
        `Disponibilidad bancaria ${comoFecha(d.fecha).replaceAll("/", "-")}.pdf`,
      )
    } catch {
      setFallo(true)
    } finally {
      setGenerando(false)
    }
  }

  return (
    <div className="hoja-reporte mx-auto max-w-6xl p-4 sm:p-8">
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-3">
        <p
          className="flex items-center gap-2 rounded-[var(--radius-control)] border border-dashed px-3 py-2 text-xs"
          style={{ ...borde, ...tenue }}
        >
          <Lock size={12} aria-hidden={true} />
          Vista de sólo lectura. El enlace caduca el {comoFecha(d.expira_en)}.
        </p>
        <button
          type="button"
          onClick={exportar}
          disabled={generando}
          className="foco-cristal inline-flex min-h-11 items-center gap-2 rounded-[var(--radius-control)] border px-4 text-sm font-medium disabled:opacity-60"
          style={borde}
        >
          <FileDown size={16} aria-hidden={true} />
          {generando ? "Generando…" : "Exportar PDF"}
        </button>
        {fallo && (
          <p role="alert" className="w-full text-xs" style={rojo}>
            No se pudo generar el PDF. Vuelve a intentarlo.
          </p>
        )}
      </div>

      <header className="mb-5 flex items-center justify-between gap-4 border-b pb-3" style={borde}>
        <div ref={marca} className="shrink-0" style={{ color: "var(--fg)" }}>
          <LogoSetieca titulo={d.empresa_nombre} className="h-12 w-auto sm:h-14" />
        </div>
        <div className="min-w-0 text-right">
          <p className="text-sm font-semibold uppercase sm:text-base">{d.empresa_nombre}</p>
          {d.empresa_rif ? (
            <p className="text-xs" style={tenue}>
              RIF {d.empresa_rif}
            </p>
          ) : null}
        </div>
      </header>

      <p className="text-xs" style={tenue}>
        Generado el {comoFechaHora(d.generado_en)}
      </p>
      <h1 className="mt-1 text-xl font-semibold">{d.titulo}</h1>

      {/* ⚠ **Cada sistema en su apartado y con su total** (13/10): el 2.0 y el 3.0
          llevan el mismo dinero mientras conviven, y sumarlos lo contaría dos veces. Sin
          el 2.0 —una foto de antes— la página es la de siempre, sin rótulos. */}
      {conDos ? <h2 className="mt-6 text-lg font-semibold">Sistema 3.0</h2> : null}
      <Bloque total={d.total} cuentas={d.cuentas} />

      {conDos ? (
        <section className="mt-10">
          <h2 className="text-lg font-semibold">Sistema 2.0</h2>
          {d.sistema2_leido ? (
            <Bloque total={d.sistema2_total} cuentas={d.sistema2_cuentas} />
          ) : (
            <p role="alert" className="mt-2 text-sm" style={rojo}>
              No se pudo leer: {d.sistema2_error}
            </p>
          )}
        </section>
      ) : null}

      {/* Al PIE y aparte, no debajo de cada cuenta (pedido del usuario, 13/10). */}
      {d.notas.length > 0 ? (
        <section className="mt-6 border-t pt-3 text-sm" style={borde}>
          <h2 className="font-semibold">Notas</h2>
          <ul className="mt-1 list-disc pl-5" style={tenue}>
            {d.notas.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}

/** El total por moneda y la tabla de cuentas de UN sistema. */
function Bloque({ total, cuentas }: { total: Saldo[]; cuentas: CuentaDisponible[] }) {
  const tenue = { color: "var(--fg-muted)" }
  const borde = { borderColor: "var(--border)" }
  const rojo = { color: "var(--destructive)" }
  return (
    <>
      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        {total.map((t) => (
          <div key={t.moneda} className="rounded-[var(--radius-control)] border p-3" style={borde}>
            <dt style={tenue}>Total en {t.moneda}</dt>
            <dd
              className="mt-1 text-base font-semibold tabular-nums"
              style={t.saldo.startsWith("-") ? rojo : undefined}
            >
              {t.moneda} {monto(t.saldo)}
            </dd>
          </div>
        ))}
      </dl>

      {cuentas.length === 0 ? (
        <p className="mt-6 text-sm" style={tenue}>
          No hay cuentas activas.
        </p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="tabla-apilable w-full text-sm">
            <thead>
              <tr className="text-left text-xs" style={tenue}>
                <th className="py-1 pr-3 font-medium">Cuenta</th>
                <th className="py-1 pr-3 font-medium">Último movimiento</th>
                <th className="py-1 text-right font-medium">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {cuentas.map((c) => (
                <tr key={`${c.cuenta}-${c.moneda}`} className="border-t align-top" style={borde}>
                  <td data-rotulo="Cuenta" className="py-2 pr-3">
                    <span className="font-medium">{c.cuenta}</span>
                  </td>
                  <td data-rotulo="Último movimiento" className="py-2 pr-3 tabular-nums">
                    {comoFecha(c.ultimo_movimiento) || "—"}
                  </td>
                  <td
                    data-rotulo="Saldo"
                    className="whitespace-nowrap py-2 text-right font-semibold tabular-nums"
                    style={c.saldo.startsWith("-") ? rojo : undefined}
                  >
                    {c.moneda} {monto(c.saldo)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
